import React from 'react';
import { Select, select, t, Trans } from '@lingui/macro';

import FrequentlyAskedQuestions from '@rover/components/src/FrequentlyAskedQuestions';
import { Link, Paragraph } from '@rover/kibble/core';

import { ServiceFaq } from './constants';
import FirstFAQIsOpen from './FirstFAQIsOpen';
import FrequentlyAskedQuestion from './FrequentlyAskedQuestion';
import UnorderedList, { ListItem } from './UnorderedList';
import useFacts from './useFacts';

const PetBoardingFaqs: React.FC<ServiceFaq> = ({
  localizedLinkUrls: links,
  facts,
  locationName,
  localityType,
  htmlRef,
}) => {
  // we create these two variables so translators can distinguish between them in the lingui selects
  const cityName = locationName;
  const neighborhoodName = locationName;

  const {
    avgRepeatCustomerAmongSittersWithRepeatNum,
    maxSearchableSittersNum,
    medianListPricePerUnitLocalCurrencyNum,
    sittersCanProvideDailyExercisePct,
    sittersCanProvideMedicationPct,
    sittersCanProvideSpecialCarePct,
    sittersWithMinResponseTimeIn60minPct,
    yearsOfExperienceNum,
  } = useFacts(facts);

  return (
    <FrequentlyAskedQuestions
      htmlRef={htmlRef}
      data-testid="pet-boarding-faqs"
      mt={['6x', '16x']}
      pb={['4x', '8x']}
    >
      <FrequentlyAskedQuestions.Header webProps={{ responsive: false }}>
        <Trans>Pet Boarding FAQs</Trans>
      </FrequentlyAskedQuestions.Header>
      <FrequentlyAskedQuestions.Subheader>
        <Trans>Frequently asked questions about Rover</Trans>
      </FrequentlyAskedQuestions.Subheader>
      <FirstFAQIsOpen>
        {/* 1 */}
        {medianListPricePerUnitLocalCurrencyNum &&
          'whatAreTheServiceFees' in links &&
          (() => {
            const { yearMonthLastUpdated, asCurrency: formattedAmount } =
              medianListPricePerUnitLocalCurrencyNum;

            return (
              <FrequentlyAskedQuestion
                question={select(localityType, {
                  city: `How much does it cost to hire a sitter for pet boarding in ${cityName}?`,
                  neighborhood: `How much does it cost to hire a sitter for pet boarding in ${neighborhoodName}?`,
                  other: `How much does it cost to hire a ${locationName} sitter for pet boarding?`,
                })}
              >
                <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
                  <Select
                    value={localityType}
                    _city={
                      <Trans>
                        Sitters on Rover are free to set their rates. The median cost to hire a
                        sitter for pet boarding in {cityName} on Rover as of {yearMonthLastUpdated}{' '}
                        was about {formattedAmount} per night, after factoring in{' '}
                        <Link href={links.whatAreTheServiceFees} target="_blank">
                          Rover’s service fees
                        </Link>
                        . A sitter’s rate may also change as you customize your booking to fit your
                        and your pet’s needs.
                      </Trans>
                    }
                    _neighborhood={
                      <Trans>
                        Sitters on Rover are free to set their rates. The median cost to hire a
                        sitter for pet boarding in {neighborhoodName} on Rover as of{' '}
                        {yearMonthLastUpdated} was about {formattedAmount} per night, after
                        factoring in{' '}
                        <Link href={links.whatAreTheServiceFees} target="_blank">
                          Rover’s service fees
                        </Link>
                        . A sitter’s rate may also change as you customize your booking to fit your
                        and your pet’s needs.
                      </Trans>
                    }
                    other={
                      <Trans>
                        Sitters on Rover are free to set their rates. The median cost to hire a{' '}
                        {locationName} sitter for pet boarding in this area on Rover as of{' '}
                        {yearMonthLastUpdated} was about {formattedAmount} per night, after
                        factoring in{' '}
                        <Link href={links.whatAreTheServiceFees} target="_blank">
                          Rover’s service fees
                        </Link>
                        . A sitter’s rate may also change as you customize your booking to fit your
                        and your pet’s needs.
                      </Trans>
                    }
                  />
                </Paragraph>
              </FrequentlyAskedQuestion>
            );
          })()}

        {/* 2 */}
        {'howDoIContactAPetSitterOrDogWalkerRoverApp' in links &&
          'howDoIContactAPetSitterOrDogWalkerComputer' in links && (
            <FrequentlyAskedQuestion
              question={t`How do I contact a ${locationName} sitter for pet boarding?`}
            >
              <>
                <Paragraph webProps={{ maxWidth: 'none' }}>
                  <Trans>
                    If you’re searching for a {locationName} pet boarding sitter for the first time,
                    visit the sitter’s profile and select the Contact button.
                  </Trans>
                </Paragraph>
                <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
                  <Trans>
                    If you have an active request or have booked a service with a sitter before,
                    learn more about how to do this in the{' '}
                    <Link href={links.howDoIContactAPetSitterOrDogWalkerRoverApp} target="_blank">
                      Rover app
                    </Link>{' '}
                    or on your{' '}
                    <Link href={links.howDoIContactAPetSitterOrDogWalkerComputer} target="_blank">
                      computer
                    </Link>
                    .
                  </Trans>
                </Paragraph>
              </>
            </FrequentlyAskedQuestion>
          )}

        {/* 3 */}
        {maxSearchableSittersNum &&
          (() => {
            const { yearMonthLastUpdated, asNumber: sittersCount } = maxSearchableSittersNum;

            return (
              <FrequentlyAskedQuestion
                question={select(localityType, {
                  city: `How many sitters provide pet boarding in ${cityName}?`,
                  neighborhood: `How many sitters provide pet boarding in ${neighborhoodName}?`,
                  other: `How many ${locationName} sitters provide pet boarding?`,
                })}
              >
                <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
                  <Select
                    value={localityType}
                    _city={
                      <Trans>
                        As of {yearMonthLastUpdated}, {sittersCount} sitters provided pet boarding
                        in {cityName}. You can filter, sort, expand your radius, read reviews, and
                        compare pricing to find the perfect sitter near you. As a reminder, pet
                        boarding sitters joining Rover must pass a background check for you and your
                        pet’s safety.
                      </Trans>
                    }
                    _neighborhood={
                      <Trans>
                        As of {yearMonthLastUpdated}, {sittersCount} sitters provided pet boarding
                        in {neighborhoodName}. You can filter, sort, expand your radius, read
                        reviews, and compare pricing to find the perfect sitter near you. As a
                        reminder, pet boarding sitters joining Rover must pass a background check
                        for you and your pet’s safety.
                      </Trans>
                    }
                    other={
                      <Trans>
                        As of {yearMonthLastUpdated}, {sittersCount} {locationName} sitters provided
                        pet boarding. You can filter, sort, expand your radius, read reviews, and
                        compare pricing to find the perfect sitter near you. As a reminder, pet
                        boarding sitters joining Rover must pass a background check for you and your
                        pet’s safety.
                      </Trans>
                    }
                  />
                </Paragraph>
              </FrequentlyAskedQuestion>
            );
          })()}

        {/* 4 */}
        <FrequentlyAskedQuestion
          question={select(localityType, {
            city: `How long will it take for me to find a pet boarding sitter in ${cityName}?`,
            neighborhood: `How long will it take for me to find a pet boarding sitter in ${neighborhoodName}?`,
            other: `How long will it take for me to find a ${locationName} pet boarding sitter?`,
          })}
        >
          {sittersWithMinResponseTimeIn60minPct ? (
            (() => {
              const percent = sittersWithMinResponseTimeIn60minPct.asPercent;
              return (
                <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
                  <Trans>
                    Rover makes it easy to reach out to multiple sitters about your booking.
                    Typically, {percent} of {locationName} pet boarding sitters respond in under an
                    hour.
                  </Trans>
                </Paragraph>
              );
            })()
          ) : (
            <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
              <Trans>
                Typically, pet boarding sitters on Rover respond in under an hour, so you should be
                matched with the perfect sitter for you and your pet in no time!
              </Trans>
            </Paragraph>
          )}
        </FrequentlyAskedQuestion>

        {/* 5 */}
        <FrequentlyAskedQuestion
          question={select(localityType, {
            city: `How experienced are sitters providing pet boarding in ${cityName}?`,
            neighborhood: `How experienced are sitters providing pet boarding in ${neighborhoodName}?`,
            other: `How experienced are ${locationName} sitters providing pet boarding?`,
          })}
        >
          <>
            {/* we have num years of experience and avg repeat customers */}
            {yearsOfExperienceNum &&
              avgRepeatCustomerAmongSittersWithRepeatNum &&
              (() => {
                const yearsOfExperience = yearsOfExperienceNum.raw;
                const repeatCustomersCount = avgRepeatCustomerAmongSittersWithRepeatNum.asNumber;
                return (
                  <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
                    <Select
                      value={localityType}
                      _city={
                        <Trans>
                          Sitters who provide pet boarding in {cityName} have an average of{' '}
                          {yearsOfExperience} years of experience. Pet boarding sitters with repeat
                          customers have an average of {repeatCustomersCount} repeat clients.
                          Experience can vary from sitter to sitter, but you can view reviews, years
                          of experience, and number of repeat clients when you compare sitters in{' '}
                          {locationName}.
                        </Trans>
                      }
                      _neighborhood={
                        <Trans>
                          Sitters who provide pet boarding in {neighborhoodName} have an average of{' '}
                          {yearsOfExperience} years of experience. Pet boarding sitters with repeat
                          customers have an average of {repeatCustomersCount} repeat clients.
                          Experience can vary from sitter to sitter, but you can view reviews, years
                          of experience, and number of repeat clients when you compare sitters in{' '}
                          {locationName}.
                        </Trans>
                      }
                      other={
                        <Trans>
                          {locationName} sitters who provide pet boarding have an average of{' '}
                          {yearsOfExperience} years of experience. Pet boarding sitters with repeat
                          customers have an average of {repeatCustomersCount} repeat clients.
                          Experience can vary from sitter to sitter, but you can view reviews, years
                          of experience, and number of repeat clients when you compare sitters.
                        </Trans>
                      }
                    />
                  </Paragraph>
                );
              })()}

            {/* we have avg repeat customers, but not num years of experience */}
            {!yearsOfExperienceNum &&
              avgRepeatCustomerAmongSittersWithRepeatNum &&
              (() => {
                const repeatCustomersCount = avgRepeatCustomerAmongSittersWithRepeatNum.asNumber;
                return (
                  <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
                    <Select
                      value={localityType}
                      _city={
                        <Trans>
                          Pet boarding sitters with repeat customers have an average of{' '}
                          {repeatCustomersCount} repeat clients. Experience can vary from sitter to
                          sitter, but you can view reviews, years of experience, and number of
                          repeat clients when you compare sitters in {cityName}.
                        </Trans>
                      }
                      _neighborhood={
                        <Trans>
                          Pet boarding sitters with repeat customers have an average of{' '}
                          {repeatCustomersCount} repeat clients. Experience can vary from sitter to
                          sitter, but you can view reviews, years of experience, and number of
                          repeat clients when you compare sitters in {neighborhoodName}.
                        </Trans>
                      }
                      other={
                        <Trans>
                          Pet boarding sitters with repeat customers have an average of{' '}
                          {repeatCustomersCount} repeat clients. Experience can vary from sitter to
                          sitter, but you can view reviews, years of experience, and number of
                          repeat clients when you compare sitters.
                        </Trans>
                      }
                    />
                  </Paragraph>
                );
              })()}

            {/* we have num years of experience, but not avg repeat customers */}
            {yearsOfExperienceNum &&
              !avgRepeatCustomerAmongSittersWithRepeatNum &&
              (() => {
                const yearsOfExperience = yearsOfExperienceNum.raw;

                return (
                  <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
                    <Select
                      value={localityType}
                      _city={
                        <Trans>
                          Sitters providing pet boarding in {cityName} have an average of{' '}
                          {yearsOfExperience} years of experience. This can vary widely from sitter
                          to sitter, but you can view reviews, years of experience, and number of
                          repeat clients when comparing sitters available in {cityName}.
                        </Trans>
                      }
                      _neighborhood={
                        <Trans>
                          Sitters providing pet boarding in {neighborhoodName} have an average of{' '}
                          {yearsOfExperience} years of experience. This can vary widely from sitter
                          to sitter, but you can view reviews, years of experience, and number of
                          repeat clients when comparing sitters available in {neighborhoodName}.
                        </Trans>
                      }
                      other={
                        <Trans>
                          {locationName} sitters providing pet boarding have an average of{' '}
                          {yearsOfExperience} years of experience. This can vary widely from sitter
                          to sitter, but you can view reviews, years of experience, and number of
                          repeat clients when comparing sitters.
                        </Trans>
                      }
                    />
                  </Paragraph>
                );
              })()}

            {/* we don't have num years of experience or avg repeat customers */}
            {!yearsOfExperienceNum && !avgRepeatCustomerAmongSittersWithRepeatNum && (
              <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
                <Select
                  value={localityType}
                  _city={
                    <Trans>
                      Experience can vary widely from sitter to sitter, but you can view reviews,
                      years of experience, and number of repeat clients when comparing sitters
                      available in {cityName}.
                    </Trans>
                  }
                  _neighborhood={
                    <Trans>
                      Experience can vary widely from sitter to sitter, but you can view reviews,
                      years of experience, and number of repeat clients when comparing sitters
                      available in {neighborhoodName}.
                    </Trans>
                  }
                  other={
                    <Trans>
                      Experience can vary widely from sitter to sitter, but you can view reviews,
                      years of experience, and number of repeat clients when comparing sitters.
                    </Trans>
                  }
                />
              </Paragraph>
            )}
          </>
        </FrequentlyAskedQuestion>

        {/* 6 */}
        <FrequentlyAskedQuestion
          question={select(localityType, {
            city: `What sort of pet boarding services do sitters in ${cityName} offer on Rover?`,
            neighborhood: `What sort of pet boarding services do sitters in ${neighborhoodName} offer on Rover?`,
            other: `What sort of pet boarding services do ${locationName} sitters offer on Rover?`,
          })}
        >
          <>
            <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
              <Select
                value={localityType}
                _city={
                  <Trans>
                    Rover makes it easy to find {cityName} pet boarding sitters to provide loving
                    care from their own home. The background-checked, 5-star sitters you can find on
                    Rover will welcome your pet into their home when you’re away—whether it’s just
                    for the weekend or longer. Pet boarding is great for:
                  </Trans>
                }
                _neighborhood={
                  <Trans>
                    Rover makes it easy to find {neighborhoodName} pet boarding sitters to provide
                    loving care from their own home. The background-checked, 5-star sitters you can
                    find on Rover will welcome your pet into their home when you’re away—whether
                    it’s just for the weekend or longer. Pet boarding is great for:
                  </Trans>
                }
                other={
                  <Trans>
                    Rover makes it easy to find {locationName} pet boarding sitters to provide
                    loving care from their own home. The background-checked, 5-star sitters you can
                    find on Rover will welcome your pet into their home when you’re away—whether
                    it’s just for the weekend or longer. Pet boarding is great for:
                  </Trans>
                }
              />
            </Paragraph>
            <UnorderedList>
              <ListItem>
                <Trans comment="Part of a list describing who would benefit from a particular service type">
                  Dogs and cats of all ages and abilities, including puppies and kittens
                </Trans>
              </ListItem>
              <ListItem>
                <Trans comment="Part of a list describing who would benefit from a particular service type">
                  Pet parents looking for a safe, loving alternative to a kennel or hotel
                </Trans>
              </ListItem>
              <ListItem>
                <Trans comment="Part of a list describing who would benefit from a particular service type">
                  Pets who’d love to socialize with their sitter’s pets
                </Trans>
              </ListItem>
            </UnorderedList>
          </>
        </FrequentlyAskedQuestion>

        {/* 7 */}
        {maxSearchableSittersNum && (
          <FrequentlyAskedQuestion
            question={t`My pet has special needs. Can I find pet boarding on Rover that can accommodate my pet’s needs?`}
          >
            {sittersCanProvideSpecialCarePct &&
            sittersCanProvideDailyExercisePct &&
            sittersCanProvideMedicationPct
              ? (() => {
                  const sitterCount = maxSearchableSittersNum.asNumber;
                  const dailyExercisePercent = sittersCanProvideDailyExercisePct.asPercent;
                  const provideMedicationPercent = sittersCanProvideMedicationPct.asPercent;
                  const specialCarePercent = sittersCanProvideSpecialCarePct.asPercent;
                  return (
                    <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
                      <Select
                        value={localityType}
                        _city={
                          <Trans>
                            There are {sitterCount} pet boarding sitters in {cityName} on Rover, so
                            you’re sure to find the perfect match for your pet! In {cityName},{' '}
                            {dailyExercisePercent} of sitters offer daily exercise,{' '}
                            {provideMedicationPercent} offer medication administration, and{' '}
                            {specialCarePercent} have experience with senior pets and pets with
                            special needs. We know each pet is unique and may have specific care
                            instructions. You can filter, sort, expand your radius, read reviews,
                            and compare pricing to find the perfect pet boarding sitter near you. As
                            a reminder, sitters joining Rover must pass a background check for you
                            and your pet’s safety.
                          </Trans>
                        }
                        _neighborhood={
                          <Trans>
                            There are {sitterCount} pet boarding sitters in {neighborhoodName} on
                            Rover, so you’re sure to find the perfect match for your pet! In{' '}
                            {neighborhoodName}, {dailyExercisePercent} of sitters offer daily
                            exercise, {provideMedicationPercent} offer medication administration,
                            and {specialCarePercent} have experience with senior pets and pets with
                            special needs. We know each pet is unique and may have specific care
                            instructions. You can filter, sort, expand your radius, read reviews,
                            and compare pricing to find the perfect pet boarding sitter near you. As
                            a reminder, sitters joining Rover must pass a background check for you
                            and your pet’s safety.
                          </Trans>
                        }
                        other={
                          <Trans>
                            There are {sitterCount} {locationName} pet boarding sitters on Rover, so
                            you’re sure to find the perfect match for your pet!{' '}
                            {dailyExercisePercent} of {locationName} sitters offer daily exercise,{' '}
                            {provideMedicationPercent} offer medication administration, and{' '}
                            {specialCarePercent} have experience with senior pets and pets with
                            special needs. We know each pet is unique and may have specific care
                            instructions. You can filter, sort, expand your radius, read reviews,
                            and compare pricing to find the perfect pet boarding sitter near you. As
                            a reminder, sitters joining Rover must pass a background check for you
                            and your pet’s safety.
                          </Trans>
                        }
                      />
                    </Paragraph>
                  );
                })()
              : (() => {
                  const sitterCount = maxSearchableSittersNum.asNumber;
                  return (
                    <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
                      <Select
                        value={localityType}
                        _city={
                          <Trans>
                            There are {sitterCount} pet boarding sitters in {cityName} on Rover, so
                            you’re sure to find the perfect match for your pet! We understand that
                            each pet is unique and may require specific instructions. You can
                            filter, sort, expand your radius, read reviews, and compare pricing to
                            find the perfect pet boarding sitter near you. As a reminder, sitters
                            joining Rover must pass a background check for you and your pet’s
                            safety.
                          </Trans>
                        }
                        _neighborhood={
                          <Trans>
                            There are {sitterCount} pet boarding sitters in {neighborhoodName} on
                            Rover, so you’re sure to find the perfect match for your pet! We
                            understand that each pet is unique and may require specific
                            instructions. You can filter, sort, expand your radius, read reviews,
                            and compare pricing to find the perfect pet boarding sitter near you. As
                            a reminder, sitters joining Rover must pass a background check for you
                            and your pet’s safety.
                          </Trans>
                        }
                        other={
                          <Trans>
                            There are {sitterCount} {locationName} pet boarding sitters on Rover, so
                            you’re sure to find the perfect match for your pet! We understand that
                            each pet is unique and may require specific instructions. You can
                            filter, sort, expand your radius, read reviews, and compare pricing to
                            find the perfect pet boarding sitter near you. As a reminder, sitters
                            joining Rover must pass a background check for you and your pet’s
                            safety.
                          </Trans>
                        }
                      />
                    </Paragraph>
                  );
                })()}
          </FrequentlyAskedQuestion>
        )}

        {/* 8 */}
        <FrequentlyAskedQuestion
          question={select(localityType, {
            city: `Do sitters providing pet boarding in ${cityName} pass background checks?`,
            neighborhood: `Do sitters providing pet boarding in ${neighborhoodName} pass background checks?`,
            other: `Do ${locationName} sitters providing pet boarding pass background checks?`,
          })}
        >
          <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
            <Trans>
              Yes! Sitters joining Rover must pass a background check before offering their
              services. You can also easily keep in touch with your pet boarding sitter through
              messages on Rover for delightful photo updates. The Rover team is available 24/7, and
              your sitter has access to advice from qualified veterinary professionals. In the rare
              event of any issues during a booking, rest easy knowing your pet is covered by the
              Rover Guarantee reimbursement program for eligible veterinary care.
            </Trans>
          </Paragraph>
        </FrequentlyAskedQuestion>

        {/* 9 */}
        {'howDoICancelABookingAndReceiveARefundRoverApp' in links &&
          'howDoICancelABookingAndReceiveARefundComputer' in links && (
            <FrequentlyAskedQuestion question="How do I cancel a pet boarding service and receive a refund?">
              <>
                <Paragraph webProps={{ maxWidth: 'none' }}>
                  <i>
                    <Trans>
                      <strong>Note:</strong> If your pet boarding sitter requested to cancel this
                      booking, then they need to cancel from their account. Please contact them via{' '}
                      <Link href="/account/inbox/" target="_blank">
                        Rover Messages
                      </Link>
                      .
                    </Trans>
                  </i>
                </Paragraph>
                <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
                  <Trans>
                    We get it—sometimes plans change and you may need to cancel your pet boarding
                    service. You can cancel an upcoming booking directly from the{' '}
                    <Link
                      href={links.howDoICancelABookingAndReceiveARefundRoverApp}
                      target="_blank"
                    >
                      Rover app
                    </Link>{' '}
                    or your{' '}
                    <Link
                      href={links.howDoICancelABookingAndReceiveARefundComputer}
                      target="_blank"
                    >
                      computer
                    </Link>
                    , in accordance with your sitter’s cancellation policy.
                  </Trans>
                </Paragraph>
              </>
            </FrequentlyAskedQuestion>
          )}
      </FirstFAQIsOpen>
    </FrequentlyAskedQuestions>
  );
};

export default PetBoardingFaqs;
